import React, { useState } from 'react';
import { instance } from '../api/axiosInstance';
import { FaStarOfLife } from 'react-icons/fa6';
import { FaRegCheckCircle } from 'react-icons/fa';
import Modal from './Modal';
import LoyaltyCard from "../pages/users/signup/LoyaltyCard";
import EmailPWVerificationForm from "../pages/users/signup/EmailPWVerificationForm";

interface FindPwProps {
  onSuccess?: () => void;
  onError?: (message: string) => void;
}

const FindPw: React.FC<FindPwProps> = ({ onSuccess, onError }) => {
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // 이메일 인증 관련 상태
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isEmailVerified, setIsEmailVerified] = useState(false);
  const [verifiedEmail, setVerifiedEmail] = useState('');

  // 이메일 인증 모달 열기/닫기 함수
  const openModal = () => setIsModalOpen(true);
  const closeModal = () => setIsModalOpen(false);

  // 이메일 인증 완료 시 호출되는 함수
  const handleEmailVerify = (email: string) => {
    setIsEmailVerified(true);
    setVerifiedEmail(email);
    closeModal();
  };

  // 비밀번호 재설정 핸들러
  const handleResetPassword = async (e: React.FormEvent) => {
    e.preventDefault();
    setSuccessMessage(null);
    setError(null);

    // 기본 유효성 검사
    if (!verifiedEmail || !newPassword || !confirmPassword) {
      setError('모든 필드를 입력해주세요.');
      if (onError) onError('모든 필드를 입력해주세요.');
      return;
    }

    if (newPassword !== confirmPassword) {
      setError('비밀번호가 일치하지 않습니다.');
      if (onError) onError('비밀번호가 일치하지 않습니다.');
      return;
    }

    setLoading(true);
    try {
      const response = await instance.post('/api/users/change-password', {
        email: verifiedEmail,
        newPassword,
        confirmPassword,
      });
      setSuccessMessage('비밀번호가 성공적으로 재설정되었습니다.');
      if (onSuccess) onSuccess();
      // 폼 초기화
      setVerifiedEmail('');
      setIsEmailVerified(false);
      setNewPassword('');
      setConfirmPassword('');
    } catch (err: any) {
      if (err.response) {
        setError(err.response.data.message || '문제가 발생했습니다.');
        if (onError) onError(err.response.data.message || '문제가 발생했습니다.');
      } else {
        setError('오류가 발생했습니다.');
        if (onError) onError('오류가 발생했습니다.');
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="mobile:px-4">
      <div className="my-10">
        <h2 className="font-semibold text-xl mb-3 text-center">
          비밀번호 재설정
        </h2>
        <p className="font-medium text-[#747474] text-sm text-center mb-10">
          가입된 이메일을 인증하고, 새로운 비밀번호를 설정하세요.
        </p>
      </div>

      <form onSubmit={handleResetPassword}>
        {/* Conditional rendering based on email verification status */}
        {!isEmailVerified ? (
          <div className="flex gap-4 font-bold text-xl mobile:text-base justify-center">
            <LoyaltyCard
              title="이메일 인증"
              icon={
                <FaRegCheckCircle className="text-green-500 text-4xl mobile:text-3xl mb-2" />
              }
              onClick={openModal} // 클릭 시 모달 오픈
            />
          </div>
        ) : (
          <>
            {/* 비밀번호 입력 필드 */}
            <label
              htmlFor="newPassword"
              className="text-[#111] font-medium mb-2 text-base flex gap-1"
            >
              새 비밀번호
              <span>
                <FaStarOfLife className="text-[#ef4444] text-[8px]" />
              </span>
            </label>
            <input
              type="password"
              id="newPassword"
              placeholder="새 비밀번호"
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              className="w-full px-4 py-4 border-b placeholder:text-[#c8c8c8c8] text-MainTextColor focus:outline-none mb-4"
              required
            />

            <label
              htmlFor="confirmPassword"
              className="text-[#111] font-medium mb-2 text-base flex gap-1"
            >
              비밀번호 확인
              <span>
                <FaStarOfLife className="text-[#ef4444] text-[8px]" />
              </span>
            </label>
            <input
              type="password"
              id="confirmPassword"
              placeholder="비밀번호 확인"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              className="w-full px-4 py-4 border-b placeholder:text-[#c8c8c8c8] text-MainTextColor focus:outline-none mb-4"
              required
            />

            <button
              type="submit"
              className="bg-[#04c2c5] text-white w-full mt-10 tracking-wide shadow-sm font-bold text-base py-4 rounded-full hover:bg-opacity-70 transition duration-300"
              disabled={loading} // 로딩 중이면 비활성화
            >
              {loading ? '재설정 중...' : '비밀번호 재설정'}
            </button>
          </>
        )}

        {error && (
          <div className="p-4 bg-red-100 text-red-700 mt-4 rounded">
            {error}
          </div>
        )}

        {successMessage && (
          <div className="p-4 bg-green-100 text-green-700 mt-4 rounded">
            {successMessage}
          </div>
        )}
      </form>

        {/* 이메일 인증 모달 */}
        <Modal
          isOpen={isModalOpen}
          onClose={closeModal}
          width="500px"
          height="280px"
        >
          <EmailPWVerificationForm
            onEmailVerify={(email: string) => {
              handleEmailVerify(email);
            }}
          />
        </Modal>
    </div>
  );
};

export default FindPw;
